import React, { useEffect, useState } from "react"
import { Toasts } from "../../helpers/helpers";
import { deleteUser, EmailAuthProvider, getAuth, reauthenticateWithCredential, signOut, updateEmail, updatePassword, updateProfile } from "firebase/auth";
import { useAuth } from "../../hooks/AuthContext";

import Modal from "../../components/Modal";

const DeleteAccountModal = ({
    show,
    onClose,
    reauthPassword,
    setReauthPassword,
    handleDeleteAccount
}: {
    show: boolean;
    onClose: () => void;
    reauthPassword: string;
    setReauthPassword: (value: string) => void;
    handleDeleteAccount: () => void;
}) => {
    return (
        <Modal isOpen={show} onClose={onClose} title="Delete Account">
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <p>Please enter your password to confirm.</p>
            <input type="password" placeholder="Enter your password" style={{ width: "100%", marginBottom: "1rem" }} value={reauthPassword} onChange={(e) => setReauthPassword(e.target.value)} />
            <button className="primary-btn" onClick={handleDeleteAccount}>Delete</button>
        </Modal>
    );
}

const Account = () => {
    const { authUser } = useAuth();
    const [showReauthModal, setReauthShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);
    const [reauthPassword, setReauthPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    useEffect(() => {
        if (authUser) {
            setDisplayName(authUser.displayName || authUser.email || "");
            setEmail(authUser.email || "");
        }
    }, [authUser]);

    if (!authUser) {
        return <div className="settings">No user found.</div>;
    }

    const ReauthModal = () => {
        return (
            <Modal isOpen={showReauthModal} onClose={() => setReauthShowModal(false)} title="Reauthentication Required">
                <p>For your security, please enter your password to continue.</p>
                <input type="password" placeholder="Enter your password" style={{ width: "100%", marginBottom: "1rem" }} value={reauthPassword} onChange={(e) => setReauthPassword(e.target.value)} />
                <button className="primary-btn" onClick={handleReauth}>Confirm</button>
            </Modal>
        );
    }

    const handleReauth = async () => {
        try {
            const credential = EmailAuthProvider.credential(authUser.email!, reauthPassword);
            await reauthenticateWithCredential(authUser, credential);
            setReauthShowModal(false);
            setReauthPassword("");

            if (pendingAction) {
                await pendingAction();
                setPendingAction(null);
            }
        } catch {
            Toasts.error("Reauthentication failed. Please try again.");
        }
    }

    const saveDisplayName = async () => {
        if (!displayName.trim()) {
            Toasts.error("Display name cannot be empty.");
            return;
        }

        try {
            await updateProfile(authUser, { 
                displayName: displayName.trim()
            });
            Toasts.success("Display name updated successfully.");
        } catch {
            Toasts.error("Failed to update display name.");
        }
    };

    const saveEmail = async () => {
        if (!email.trim()) {
            Toasts.error("Email cannot be empty.");
            return;
        }

        const action = async () => {
            await updateEmail(authUser, email.trim());
            Toasts.success("Email updated successfully.");
        }

        try {
            await action();
        } catch (error: any) {
            if (error.code === 'auth/requires-recent-login') {
                setPendingAction(() => action);
                setReauthShowModal(true);
            } else {
                Toasts.error("Failed to update email.");
            }
        }
    };

    const savePassword = async () => {
        if (!newPassword.trim() || !confirmPassword.trim()) {
            Toasts.error("Please fill in all password fields.");
            return;
        }

        if (newPassword !== confirmPassword) {
            Toasts.error("Passwords do not match!");
            return;
        }

        const action = async () => {
            await updatePassword(authUser, newPassword.trim());
            Toasts.success("Password updated successfully.");
            setNewPassword("");
            setConfirmPassword("");
        }

        try {
            await action();
        } catch (error: any) {
            if (error.code === 'auth/requires-recent-login') {
                setPendingAction(() => action);
                setReauthShowModal(true);
            } else {
                Toasts.error("Failed to update password.");
            }
        }
    };

    const handleDeleteAccount = async () => {
        if (!reauthPassword.trim()) {
            Toasts.error("Please enter your password.");
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(authUser.email!, reauthPassword);
            await reauthenticateWithCredential(authUser, credential);

            authUser.delete();
            await deleteUser(authUser);

            Toasts.success("Account deleted successfully.");
            setShowDeleteModal(false);
            setReauthPassword("");
            document.location.href = "/";
        } catch {

        }
    }

    const handleSignOut = async () => {
        await signOut(getAuth());
        document.location.href = "/";
    }


    return (
        <section id="account">
            {showReauthModal && <ReauthModal />}
            {showDeleteModal && <DeleteAccountModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                reauthPassword={reauthPassword}
                setReauthPassword={setReauthPassword}
                handleDeleteAccount={handleDeleteAccount}
            />}

            <h3>Account</h3>

            <div className="setting-item">
                <label>Display Name</label>
                <input type="text" placeholder="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                <button onClick={saveDisplayName}>Save</button>
            </div>

            <div className="setting-item">
                <label>Email</label>
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button onClick={saveEmail}>Save</button>
            </div>

            <div className="setting-item">
                <label>Password</label>
                <input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                <button onClick={savePassword}>Change Password</button>
            </div>

            <div className="setting-item">
                <label>Logout</label>
                <button className="logout-btn" onClick={handleSignOut}>Logout</button>
            </div>

            <div className="danger-zone">
                <h3>Danger Zone</h3>
                <div className="setting-item">
                    <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>Delete Account</button>
                </div>
            </div>
        </section>
    )
}

export default Account;
